
import { useEffect, useRef } from "react";
import { toast } from "sonner";

import { ApiError, exchangeGoogleIdToken, type LoginResult } from "@/lib/api";
import { Button } from "@/components/ui/button";

const CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GSI_SRC = "https://accounts.google.com/gsi/client";

interface GoogleIdApi {
  initialize: (cfg: {
    client_id: string;
    callback: (resp: { credential: string }) => void;
  }) => void;
  renderButton: (el: HTMLElement, opts: Record<string, unknown>) => void;
}
declare global {
  interface Window {
    google?: { accounts?: { id?: GoogleIdApi } };
  }
}

/**
 * "Continue with Google" via Google Identity Services (the Google credentials API) — matches the
 * Grails app. Loads the GSI client, gets an ID token, and exchanges it for a JWT on the backend.
 * Inert (shows a configure hint) until VITE_GOOGLE_CLIENT_ID is set and localhost is an
 * authorized JavaScript origin on that OAuth client.
 */
export function GoogleSignInButton({
  onToken,
  onDone,
}: {
  onToken: (result: LoginResult) => void;
  onDone: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const rendered = useRef(false);
  const cb = useRef({ onToken, onDone });

  useEffect(() => {
    cb.current = { onToken, onDone };
  });

  useEffect(() => {
    if (!CLIENT_ID) return;

    function render() {
      const id = window.google?.accounts?.id;
      if (rendered.current || !id || !ref.current) return;
      id.initialize({
        client_id: CLIENT_ID!,
        callback: async (resp) => {
          try {
            const r = await exchangeGoogleIdToken(resp.credential);
            cb.current.onToken(r);
            cb.current.onDone();
          } catch (e) {
            toast.error(e instanceof ApiError ? e.message : "Google sign-in failed");
          }
        },
      });
      id.renderButton(ref.current, {
        theme: "outline",
        size: "large",
        text: "continue_with",
        width: 300,
      });
      rendered.current = true;
    }

    if (window.google?.accounts?.id) {
      render();
      return;
    }
    const existing = document.getElementById("gsi-client");
    if (existing) {
      existing.addEventListener("load", render);
      return () => existing.removeEventListener("load", render);
    }
    const s = document.createElement("script");
    s.src = GSI_SRC;
    s.async = true;
    s.id = "gsi-client";
    s.onload = render;
    document.body.appendChild(s);
  }, []);

  if (!CLIENT_ID) {
    return (
      <Button
        variant="outline"
        className="w-full"
        onClick={() =>
          toast.error("Google sign-in isn't configured (set VITE_GOOGLE_CLIENT_ID).")
        }
      >
        Continue with Google
      </Button>
    );
  }
  return <div ref={ref} className="flex justify-center" />;
}
